import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { trTR } from '@clerk/localizations';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import App from './App.tsx';

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

if (!PUBLISHABLE_KEY) {
  console.warn('[clerk] VITE_CLERK_PUBLISHABLE_KEY eksik.');
}

const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const isProductionKey = PUBLISHABLE_KEY?.startsWith('pk_live_');

// Clerk'in canlı (pk_live_) anahtarı yalnızca onaylı domainlerde (bitmiyo.com.tr ve
// alt domainleri) çalışır — localhost'ta CORS/origin hatasıyla sessizce çöker.
// Aynı uyarı deseni bitmiyo ve soru'da da var; burada da localde geliştirirken
// boş/beyaz ekran yerine ne olduğunu açıklayan bir mesaj gösteriyoruz.
function WarningScreen({ title, message, subMessage }: { title: string; message: string; subMessage?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'Inter, system-ui, sans-serif',
        backgroundColor: '#f8fafc',
        color: '#0f172a',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <div style={{ padding: '2rem', background: '#fff', borderRadius: '1rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', color: '#dc2626' }}>
          {title}
        </h2>
        <p style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 500 }}>{message}</p>
        {subMessage && (
          <p style={{ maxWidth: '600px', lineHeight: 1.6, color: '#475569', fontSize: '14px', marginTop: '16px', padding: '1rem', background: '#f1f5f9', borderRadius: '0.5rem' }}>
            {subMessage}
          </p>
        )}
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      {!PUBLISHABLE_KEY ? (
        <WarningScreen
          title="Sistem Yapılandırması Eksik"
          message="VITE_CLERK_PUBLISHABLE_KEY çevre değişkeni bulunamadı."
          subMessage="Cloudflare Pages ortamı için Dashboard > Settings > Environment Variables bölümüne anahtarı ekleyip yeniden deploy edin."
        />
      ) : isLocalhost && isProductionKey ? (
        <WarningScreen
          title="Geliştirici Uyarısı: Localhost'ta Canlı Anahtar Kullanılamaz!"
          message="Şu anda Localhost'tasınız, ancak VITE_CLERK_PUBLISHABLE_KEY olarak bir canlı (pk_live_) anahtar kullanıyorsunuz."
          subMessage="Clerk güvenlik politikaları gereği canlı anahtarlar yalnızca onaylı domainlerde (örn. sinif.bitmiyo.com.tr) çalışır. Lütfen .env dosyanızda bir geliştirme (pk_test_...) anahtarı kullanın veya uygulamayı Cloudflare'e gönderip canlıda test edin."
        />
      ) : (
        <ClerkProvider
          localization={trTR}
          publishableKey={PUBLISHABLE_KEY}
          afterSignOutUrl={isLocalhost ? '/' : 'https://bitmiyo.com.tr'}
          appearance={{
            variables: {
              colorPrimary: '#14B8A6',
              colorText: '#17181C',
              colorBackground: '#ffffff',
              borderRadius: '0.5rem',
              fontFamily: 'Inter, system-ui, sans-serif',
            },
          }}
        >
          <App />
        </ClerkProvider>
      )}
    </HelmetProvider>
  </StrictMode>
);
